import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ManageServices = () => {
  const [services, setServices] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    features: "",
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const serviceData = {
        ...formData,
        features: formData.features.split("\n").filter((f) => f.trim()),
      };

      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, serviceData, { headers });
        toast.success("Service updated successfully!");
      } else {
        await axios.post(`${API}/services`, serviceData, { headers });
        toast.success("Service created successfully!");
      }

      setIsDialogOpen(false);
      setEditingService(null);
      setFormData({ name: "", description: "", price: "", features: "" });
      fetchServices();
    } catch (error) {
      console.error("Error saving service:", error);
      toast.error("Failed to save service");
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description,
      price: service.price,
      features: service.features.join("\n"),
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;

    const token = localStorage.getItem("adminToken");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      await axios.delete(`${API}/services/${id}`, { headers });
      toast.success("Service deleted successfully!");
      fetchServices();
    } catch (error) {
      console.error("Error deleting service:", error);
      toast.error("Failed to delete service");
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingService(null);
    setFormData({ name: "", description: "", price: "", features: "" });
  };

  return (
    <div data-testid="manage-services">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Manage Services</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-service-button">
              <Plus size={20} className="mr-2" /> Add Service
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid="service-dialog">
            <DialogHeader>
              <DialogTitle>{editingService ? "Edit Service" : "Add New Service"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Service Name</Label>
                <Input
                  id="name"
                  name="name"
                  data-testid="service-name-input"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  data-testid="service-description-input"
                  value={formData.description}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="price">Price</Label>
                <Input
                  id="price"
                  name="price"
                  data-testid="service-price-input"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="e.g., $500 - $800"
                  required
                />
              </div>
              <div>
                <Label htmlFor="features">Features (one per line)</Label>
                <Textarea
                  id="features"
                  name="features"
                  rows={5}
                  data-testid="service-features-input"
                  value={formData.features}
                  onChange={handleChange}
                  placeholder="Professional bartender\nPremium spirits\nCustom cocktails"
                  required
                />
              </div>
              <div className="flex space-x-4">
                <Button type="submit" className="flex-1" data-testid="service-submit-button">
                  {editingService ? "Update" : "Create"} Service
                </Button>
                <Button type="button" variant="outline" onClick={handleDialogClose} data-testid="service-cancel-button">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service, index) => (
          <Card key={service.id} data-testid={`service-card-${index}`}>
            <CardHeader>
              <CardTitle>{service.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-2 text-sm">{service.description}</p>
              <p className="text-2xl font-bold text-[#8B7355] mb-4">{service.price}</p>
              <ul className="text-sm space-y-1 mb-4">
                {service.features.slice(0, 3).map((feature, idx) => (
                  <li key={idx} className="text-gray-600">• {feature}</li>
                ))}
              </ul>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(service)}
                  data-testid={`edit-service-${index}`}
                >
                  <Pencil size={16} className="mr-1" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDelete(service.id)}
                  data-testid={`delete-service-${index}`}
                >
                  <Trash2 size={16} className="mr-1" /> Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {services.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600">No services yet. Click "Add Service" to create one.</p>
        </div>
      )}
    </div>
  );
};

export default ManageServices;
